import { AssistantCitation } from '@services/modules/AssistantStream'
import copy from 'copy-to-clipboard'

import { AssistantDateOptions, renderAssistantDates } from './assistantDates'

/*
 * What "Copy" puts on the clipboard for an answer: what the user sees, in two flavours.
 *   text/plain — the answer's markdown with its dates written out as they are shown (never a
 *                raw {{date:…}} token) and without what the renderer hides: a link is its
 *                words, an image is nothing, and no address of either is left;
 *   text/html  — the answer as it is rendered, so that a table pastes into Excel or Outlook as
 *                a table with its columns.
 * The HTML is rebuilt from the rendered answer, element by element, from a fixed list of
 * formatting tags. Nothing else is carried over: no class or style, no attribute that holds a
 * URL, no link or image (the renderer never produces those, and they could not get through
 * here if it did).
 */

export interface CopyPayload {
  text: string
  /** Empty when there is no rendered answer to take it from: only the text is copied. */
  html: string
}

export interface CopyOptions {
  dateFormat?: string
  /** The organisation's time zone (appStore.timezone), as for the answer on screen. */
  defaultTimeZone?: string
  /** As for the answer on screen: see AssistantDateOptions.zoneLabel. */
  zoneLabel?: AssistantDateOptions['zoneLabel']
  /** The source records shown under the answer; copied as their labels. */
  citations?: AssistantCitation[]
  /** The answer as rendered on screen (the markdown block of its bubble). */
  rendered?: Element | null
}

/** Formatting that is carried over as it is; any other element only passes its content on. */
const KEPT_TAGS: ReadonlySet<string> = new Set([
  'p',
  'br',
  'hr',
  'strong',
  'b',
  'em',
  'i',
  'del',
  's',
  'sub',
  'sup',
  'code',
  'pre',
  'blockquote',
  'ul',
  'ol',
  'li',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
])

const VOID_TAGS: ReadonlySet<string> = new Set(['br', 'hr'])

/** Elements whose content is not text of the answer either. */
const DROPPED_TAGS: ReadonlySet<string> = new Set([
  'script',
  'style',
  'template',
  'noscript',
  'img',
  'picture',
  'svg',
  'math',
  'video',
  'audio',
  'iframe',
  'frame',
  'object',
  'embed',
  'button',
  'select',
  'textarea',
  'link',
  'meta',
  'base',
])

/** A grid Excel and Outlook draw without the page's stylesheet. */
const TABLE_ATTRIBUTES =
  ' border="1" cellspacing="0" cellpadding="4" style="border-collapse:collapse"'

const ALIGNMENTS: ReadonlySet<string> = new Set(['left', 'center', 'right'])

const ELEMENT_NODE = 1
const TEXT_NODE = 3

const escapeHtml = (text: string) =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

/** A whole number written in an attribute (a column span, the start of a list), or null. */
const wholeNumber = (value: string | null): string | null =>
  value !== null && /^\d{1,4}$/.test(value) ? value : null

const cellAttributes = (cell: Element): string => {
  let attributes = ''
  for (const name of ['colspan', 'rowspan']) {
    const span = wholeNumber(cell.getAttribute(name))
    if (span !== null) {
      attributes += ` ${name}="${span}"`
    }
  }
  const align = (
    (cell as HTMLElement).style?.textAlign ||
    cell.getAttribute('align') ||
    ''
  ).toLowerCase()
  if (ALIGNMENTS.has(align)) {
    attributes += ` align="${align}"`
  }
  return attributes
}

const attributesOf = (tag: string, element: Element): string => {
  switch (tag) {
    case 'table':
      return TABLE_ATTRIBUTES
    case 'th':
    case 'td':
      return cellAttributes(element)
    case 'ol': {
      const start = wholeNumber(element.getAttribute('start'))
      return start !== null ? ` start="${start}"` : ''
    }
    default:
      return ''
  }
}

const serializeChildren = (node: Node): string => {
  let html = ''
  node.childNodes.forEach(child => {
    html += serializeNode(child)
  })
  return html
}

const serializeNode = (node: Node): string => {
  if (node.nodeType === TEXT_NODE) {
    return escapeHtml(node.textContent ?? '')
  }
  if (node.nodeType !== ELEMENT_NODE) {
    return ''
  }

  const element = node as Element
  const tag = element.tagName.toLowerCase()
  if (DROPPED_TAGS.has(tag) || element.getAttribute('aria-hidden') === 'true') {
    return ''
  }
  if (tag === 'input') {
    // A task-list item: its box, as the characters it stands for.
    return element.getAttribute('type') === 'checkbox'
      ? (element as HTMLInputElement).checked
        ? '[x]'
        : '[ ]'
      : ''
  }
  if (!KEPT_TAGS.has(tag)) {
    return serializeChildren(element)
  }
  if (VOID_TAGS.has(tag)) {
    return `<${tag}>`
  }
  return `<${tag}${attributesOf(tag, element)}>${serializeChildren(
    element
  )}</${tag}>`
}

/** "Sources: IM-1, IM-2" — the labels of the citation pills, in the order shown. */
const sourcesLine = (citations: AssistantCitation[] | undefined): string => {
  const labels = (citations ?? [])
    .map(citation => citation.label?.trim())
    .filter((label): label is string => !!label)
  return labels.length ? `Sources: ${labels.join(', ')}` : ''
}

/*
 * The parts of markdown that carry an address the renderer never shows: the target of a link
 * or an image, written next to it — [words](address "title"), ![alt](address) — or apart from
 * it as a definition — [words][label] or [label] with "[label]: address" on a line of its own.
 * Code is left alone: there the same characters are shown as they are written.
 */
const LABEL = String.raw`\[(?:[^\[\]\\]|\\.)*\]`
const DESTINATION = String.raw`(?:<[^<>\n]*>|(?:[^()\s\\]|\\.|\((?:[^()\s\\]|\\.)*\))*)`
const TITLE = String.raw`(?:\s+(?:"[^"]*"|'[^']*'|\([^()]*\)))?`
const TARGET = String.raw`\(\s*${DESTINATION}${TITLE}\s*\)`
const REFERENCE = String.raw`\[[^\[\]]*\]`
// What follows "[label]:" in a definition: one address (no blank in it, unless in <…>) and at
// most a quoted title. A line that goes on in words ("[Note]: the report is due") is no
// definition: the renderer shows it, and so it is copied.
const DEFINITION_TARGET = String.raw`(?:<[^<>\n]*>|[^\s<][^\s]*)(?:[ \t]+(?:"[^"\n]*"|'[^'\n]*'|\([^()\n]*\)))?`

const HIDDEN_ADDRESSES = new RegExp(
  [
    String.raw`(?<fence>^ {0,3}(?<mark>\`{3,}|~{3,})[^\n]*\n[\s\S]*?(?:^ {0,3}\k<mark>[^\n]*$|(?![\s\S])))`,
    String.raw`(?<code>(?<ticks>\`+)[\s\S]*?\k<ticks>)`,
    String.raw`(?<definition>^ {0,3}\[(?<defined>(?:[^\[\]\\]|\\.)+)\]:[ \t]*(?:\n[ \t]*)?${DEFINITION_TARGET}[ \t]*(?:\n|(?![\s\S])))`,
    String.raw`(?<image>!(?<alt>${LABEL})(?:(?<imageTarget>${TARGET})|(?<imageLabel>${REFERENCE}))?)`,
    String.raw`(?<link>(?<words>${LABEL})(?:(?<linkTarget>${TARGET})|(?<linkLabel>${REFERENCE})))`,
    String.raw`(?<shortcut>${LABEL})`,
  ].join('|'),
  'gm'
)

/** A label as definitions are matched: without its brackets, case and spacing aside. */
const labelKey = (label: string) =>
  label
    .replace(/^\[|\]$/g, '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase()

/** A footnote ("[^1]: …") is shown with the answer: it is not a hidden address. */
const isFootnote = (label: string) => label.startsWith('^')

/** A link around an image is found once the image inside it is gone: a few passes at most. */
const MAX_NESTING = 3

/** The markdown without the addresses of its links and images (see HIDDEN_ADDRESSES). */
const withoutHiddenAddresses = (content: string): string => {
  let text = content
  for (let pass = 0; pass < MAX_NESTING; pass++) {
    const next = removeHiddenAddresses(text)
    if (next === text) {
      break
    }
    text = next
  }
  return text
}

const removeHiddenAddresses = (content: string): string => {
  const defined = new Set<string>()
  for (const match of content.matchAll(HIDDEN_ADDRESSES)) {
    const label = match.groups?.defined
    if (label !== undefined && !isFootnote(label)) {
      defined.add(labelKey(label))
    }
  }

  return content.replace(HIDDEN_ADDRESSES, (...args) => {
    const match: string = args[0]
    const groups: Record<string, string | undefined> = args[args.length - 1]

    if (groups.definition !== undefined) {
      return isFootnote(groups.defined ?? '') ? match : ''
    }
    if (groups.image !== undefined) {
      const label = groups.imageLabel
        ? labelKey(groups.imageLabel) || labelKey(groups.alt ?? '')
        : labelKey(groups.alt ?? '')
      return groups.imageTarget !== undefined || defined.has(label) ? '' : match
    }
    if (groups.link !== undefined) {
      const words = (groups.words ?? '').slice(1, -1)
      const label = labelKey(groups.linkLabel ?? '') || labelKey(words)
      return groups.linkTarget !== undefined || defined.has(label)
        ? words
        : match
    }
    if (groups.shortcut !== undefined) {
      return defined.has(labelKey(match)) ? match.slice(1, -1) : match
    }
    // code: shown as written
    return match
  })
}

/** The answer's markdown as shown — dates written out, no hidden address — and its sources. */
export const buildCopyText = (
  content: string,
  options: CopyOptions = {}
): string => {
  const body = renderAssistantDates(withoutHiddenAddresses(content), {
    dateFormat: options.dateFormat,
    defaultTimeZone: options.defaultTimeZone,
    zoneLabel: options.zoneLabel,
  }).trim()
  const sources = sourcesLine(options.citations)
  return sources ? `${body}\n\n${sources}` : body
}

/** The rendered answer as plain formatting HTML (see the note at the top); '' without one. */
export const buildCopyHtml = (
  rendered: Element | null | undefined,
  citations?: AssistantCitation[]
): string => {
  if (!rendered) {
    return ''
  }
  const body = serializeChildren(rendered).trim()
  if (!body) {
    return ''
  }
  const sources = sourcesLine(citations)
  return sources ? `${body}<p>${escapeHtml(sources)}</p>` : body
}

export const buildCopyPayload = (
  content: string,
  options: CopyOptions = {}
): CopyPayload => ({
  text: buildCopyText(content, options),
  html: buildCopyHtml(options.rendered, options.citations),
})

/**
 * Puts the payload on the clipboard: both flavours where the browser can, else the text alone.
 * Returns whether something was copied.
 */
export const copyToClipboard = async ({
  text,
  html,
}: CopyPayload): Promise<boolean> => {
  const clipboard: Clipboard | undefined =
    typeof navigator !== 'undefined' ? navigator.clipboard : undefined

  if (
    html &&
    typeof clipboard?.write === 'function' &&
    typeof ClipboardItem !== 'undefined'
  ) {
    try {
      await clipboard.write([
        new ClipboardItem({
          'text/plain': new Blob([text], { type: 'text/plain' }),
          'text/html': new Blob([html], { type: 'text/html' }),
        }),
      ])
      return true
    } catch {
      // Refused (a browser that only writes text, a denied permission): the text alone, below.
    }
  }

  if (typeof clipboard?.writeText === 'function') {
    try {
      await clipboard.writeText(text)
      return true
    } catch {
      // No clipboard access this way either: the older way, below.
    }
  }

  // Outside a secure context there is no Clipboard API at all.
  return copy(text)
}
